import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser, PapiGallection } from '@wix/da-papi-types';
import {
  BiContextProvidedValue,
  BiData,
  DefaultBiEvent,
  ClickCopyBiEvent,
  DownloadAllClickBiEvent,
  MoreMenuClickBiEvent,
  type AddNewFolderClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import Ellipsis from '@wix/da-ds/pkg/Icons/Ellipsis';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import useBookmarksStream from '../../../hooks/useBookmarksStream';
import useCanCreateSubfolder from '../../../hooks/useCanCreateSubfolder';

export interface Props {
  className?: string;
  currentUser: PapiUser;
  folder: PapiGallection;
  featuredFolderId?: number;
  sizeOverride?: 'small' | 'medium' | 'large';
  withCreateFolder?: boolean;
  withMoveUp?: boolean;
  withBookmark?: boolean;
  onCreateClick: () => void;
  onEditClick: () => void;
  onShareClick: () => void;
  onMoveUpClick: () => void;
  onDeleteClick: () => void;
  onBookmarkAddClick: () => void;
  onBookmarkRemoveClick: () => void;
}

const FolderActionsDropdown: FC<Props> = ({
  className,
  folder,
  featuredFolderId,
  sizeOverride,
  withCreateFolder = true,
  withMoveUp,
  withBookmark,
  onCreateClick,
  onEditClick: onEditFolderClick,
  onShareClick,
  onMoveUpClick,
  onDeleteClick,
  onBookmarkAddClick,
  onBookmarkRemoveClick,
}) => {
  const { t } = useTranslation();
  const { items: bookmarks = [] } = useBookmarksStream();
  const canCreateFolder = useCanCreateSubfolder(folder);
  const isBookmarked = bookmarks.some(
    b => b.folderId === folder.folderId && b.type === folder.type
  );
  const isGallery = folder.type === 'gallery';
  const isFeaturedFolder = folder.folderId === featuredFolderId;

  const isMobile = useContext(MobileContext);
  const withDownloadLink = folder.type === 'stash';
  const dropdownItems: NullableDropdownItem[] = [
    {
      label: isGallery
        ? t('studio.published.header.folderActions.share')
        : t('studio.header.folderActions.share'),
      onClick: onShareClick,
      biData: BiData<ClickCopyBiEvent>({
        evid: 357,
        typeid: isGallery ? ResourceType.GALLERY : ResourceType.STASH_FOLDER,
        itemid: folder.folderId,
        sectionname: BiContextProvidedValue,
      }),
    },
    {
      label: isGallery
        ? t('studio.published.header.folderActions.edit')
        : t('studio.header.folderActions.edit'),
      onClick: onEditFolderClick,
    },
    withCreateFolder &&
      canCreateFolder && {
        label: isGallery
          ? t('studio.published.header.folderActions.createFolder')
          : t('studio.header.folderActions.createFolder'),
        onClick: onCreateClick,
        biData: BiData<AddNewFolderClickBiEvent>({
          evid: 40,
          typeid: isGallery ? ResourceType.GALLERY : ResourceType.STASH_FOLDER,
          itemid: folder.folderId,
          sectionname: BiContextProvidedValue,
        }),
      },
    withDownloadLink && {
      label: isGallery
        ? t('studio.published.header.folderActions.download')
        : t('studio.header.folderActions.download'),
      onClick: () =>
        window.open(Url.stashFolderZipDownload(folder.folderId), '_blank'),
      biData: BiData<DownloadAllClickBiEvent>({
        evid: 18,
        typeid: isGallery ? ResourceType.GALLERY : ResourceType.STASH_FOLDER,
        itemid: folder.folderId,
        sectionname: BiContextProvidedValue,
        view: BiContextProvidedValue,
      }),
    },
    withMoveUp && {
      label: isGallery
        ? t('studio.published.header.folderActions.moveUp')
        : t('studio.header.folderActions.moveUp'),
      onClick: onMoveUpClick,
    },
    withBookmark &&
      !isBookmarked && {
        label: isGallery
          ? t('studio.published.header.folderActions.bookmark')
          : t('studio.header.folderActions.bookmark'),
        onClick: onBookmarkAddClick,
        biData: BiData<DefaultBiEvent>({
          evid: 2,
          typeid: gallectionTypeToResourceType(folder.type),
          itemid: folder.folderId,
          sectionname: BiContextProvidedValue,
          click_info: 'add_shortcut',
        }),
      },
    withBookmark &&
      isBookmarked && {
        label: isGallery
          ? t('studio.published.header.folderActions.removeBookmark')
          : t('studio.header.folderActions.removeBookmark'),
        onClick: onBookmarkRemoveClick,
        biData: BiData<DefaultBiEvent>({
          evid: 2,
          typeid: gallectionTypeToResourceType(folder.type),
          itemid: folder.folderId,
          sectionname: BiContextProvidedValue,
          click_info: 'remove_shortcut',
        }),
      },
    !isFeaturedFolder && {
      label: isGallery
        ? t('studio.published.header.folderActions.delete')
        : t('studio.header.folderActions.delete'),
      onClick: onDeleteClick,
    },
  ];

  const size = sizeOverride || (isMobile ? 'large' : 'medium');
  const iconSize = {
    small: IconSize.SMALL,
    medium: IconSize.MEDIUM,
    large: IconSize.LARGE,
  }[size];

  /* note: wrapper div avoids layout shift because button component might render an extra div on ssr */
  return (
    <div className={className}>
      <IconButtonWithContextMenu
        icon={Ellipsis}
        iconSizeOverride={iconSize}
        aria-label={t('common.moreActions')}
        size={size}
        items={dropdownItems}
        biData={BiData<MoreMenuClickBiEvent>({
          evid: 417,
          typeid: isGallery ? ResourceType.GALLERY : ResourceType.STASH_FOLDER,
          itemid: folder.folderId,
        })}
        stopPropagation
        noPaddings
      />
    </div>
  );
};
FolderActionsDropdown.displayName = 'FolderActionsDropdown';

export default FolderActionsDropdown;
